import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, FileText, Image as ImageIcon, Mic, Paperclip, Check, Loader2, Search } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const FMT_ICON = { photo: ImageIcon, pdf: FileText, voice: Mic, document: FileText, video: Paperclip, audio: Mic, other: Paperclip };
const PAGE = 30;

// Reusable "Select from Vault" picker. Paginated + searchable so it scales to thousands of files.
export default function VaultSelector({ open, onOpenChange, matterId, multiple = true, initialSelected = [], onConfirm }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState(new Set(initialSelected));
  const skipRef = useRef(0);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);

  const load = useCallback(async (reset) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const skip = reset ? 0 : skipRef.current;
    try {
      const params = new URLSearchParams({ skip: String(skip), limit: String(PAGE) });
      if (matterId) params.set("matter_id", matterId);
      if (search.trim()) params.set("q", search.trim());
      const { data } = await api.get(`/evidence/page?${params.toString()}`);
      setTotal(data.total);
      setHasMore(data.has_more);
      skipRef.current = skip + data.items.length;
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
    } catch { toast.error("Could not load your Vault"); }
    finally { loadingRef.current = false; setLoading(false); }
  }, [matterId, search]);

  useEffect(() => {
    if (!open) return;
    setChosen(new Set(initialSelected));
    skipRef.current = 0;
    const t = setTimeout(() => load(true), search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search, matterId]);

  useEffect(() => {
    if (!open || !hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => {
      if (e[0].isIntersecting && hasMore && !loadingRef.current) load(false);
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [open, hasMore, load]);

  const toggle = (id) => {
    setChosen((prev) => {
      const next = new Set(multiple ? prev : []);
      if (prev.has(id) && multiple) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirm = () => {
    onConfirm(Array.from(chosen));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="vault-selector">
        <DialogHeader>
          <DialogTitle className="font-head font-semibold tracking-tight flex items-center gap-2">
            <FolderOpen className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Select from Vault
          </DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            Reuse evidence you've already added — no need to upload it again.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ek-text-muted)" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files…"
            data-testid="vault-selector-search"
            className="w-full rounded-md border pl-9 pr-3 py-2 text-sm"
            style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)", color: "var(--ek-text)" }}
          />
        </div>

        <div className="max-h-[46vh] overflow-y-auto border rounded-md divide-y" style={{ borderColor: "var(--ek-border)" }}>
          {items.length === 0 && !loading ? (
            <p className="text-sm text-center py-8" style={{ color: "var(--ek-text-muted)" }} data-testid="vault-selector-empty">
              {search ? `No files match “${search}”.` : "Your Vault is empty for this case."}
            </p>
          ) : items.map((e) => {
            const Icon = FMT_ICON[e.format] || Paperclip;
            const on = chosen.has(e.id);
            return (
              <button
                key={e.id} type="button" onClick={() => toggle(e.id)}
                data-testid={`vault-selector-item-${e.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/[0.03]"
                style={{ background: on ? "var(--ek-surface-hover)" : "transparent" }}
              >
                <span className="grid place-items-center h-5 w-5 rounded border shrink-0" style={{ borderColor: on ? "var(--ek-secure)" : "var(--ek-border)", background: on ? "var(--ek-secure)" : "transparent" }}>
                  {on && <Check className="h-3.5 w-3.5 text-white" />}
                </span>
                <Icon className="h-4 w-4 shrink-0" style={{ color: "var(--ek-text-muted)" }} />
                <span className="text-sm flex-1 min-w-0 truncate" data-private>{e.filename || "Untitled"}</span>
                <span className="ek-mono-label shrink-0">{(e.format || "").toUpperCase()}</span>
              </button>
            );
          })}
          <div ref={sentinelRef} className="py-3 text-center">
            {loading && <Loader2 className="h-4 w-4 animate-spin mx-auto" style={{ color: "var(--ek-primary)" }} />}
            {!loading && hasMore && (
              <button onClick={() => load(false)} data-testid="vault-selector-load-more" className="ek-mono-label" style={{ color: "var(--ek-primary)" }}>
                Load more
              </button>
            )}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <span className="text-xs" style={{ color: "var(--ek-text-muted)" }}>{chosen.size} selected · {total} in Vault</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>Cancel</Button>
            <Button onClick={confirm} data-testid="vault-selector-confirm" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              {multiple ? `Attach ${chosen.size || ""}`.trim() : "Select"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
